import { useState } from 'react';
import {
  Alert,
  AlertDescription,
  Button,
  Label,
  RadioGroup,
  RadioGroupItem,
  Spinner,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@monksflow/monks-ui';
import { AlertTriangle, Calendar, ChevronDown, Trash2 } from 'lucide-react';
import { useClearWorkspaceOpportunities } from '@/features/admin/model/useClearWorkspaceOpportunities';
import { useOpportunitiesCountPreview } from '@/features/admin/model/useOpportunitiesCountPreview';
import { ClearOpportunitiesConfirmationModal } from './ClearOpportunitiesConfirmationModal';
import { DatePicker } from '@/shared/ui';
import { useTranslation } from 'react-i18next';

interface ClearWorkspaceOpportunitiesActionProps {
  workspaceId: string;
  workspaceName: string;
}

export const ClearWorkspaceOpportunitiesAction = ({
  workspaceId,
  workspaceName,
}: ClearWorkspaceOpportunitiesActionProps) => {
  const { t } = useTranslation('admin');

  const [isOpen, setIsOpen] = useState(false);
  const [deletionMode, setDeletionMode] = useState<'dateRange' | 'all'>('all');
  const [dateStart, setDateStart] = useState<Date | null>(null);
  const [dateEnd, setDateEnd] = useState<Date | null>(null);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

  // Fetch preview count based on workspace and selection
  const { data: previewCount, isLoading: isLoadingPreview } = useOpportunitiesCountPreview({
    workspaceId,
    dateStart: deletionMode === 'dateRange' ? dateStart : undefined,
    dateEnd: deletionMode === 'dateRange' ? dateEnd : undefined,
  });

  const clearMutation = useClearWorkspaceOpportunities();

  const canClear = deletionMode === 'all' || (dateStart !== null && dateEnd !== null);

  const handleClearClick = () => {
    setIsConfirmModalOpen(true);
  };

  const handleConfirmDelete = () => {
    clearMutation.mutate(
      {
        workspaceId,
        workspaceName,
        dateStart: deletionMode === 'dateRange' ? (dateStart ?? undefined) : undefined,
        dateEnd: deletionMode === 'dateRange' ? (dateEnd ?? undefined) : undefined,
      },
      {
        onSuccess: () => {
          setIsConfirmModalOpen(false);
          // Reset form
          setDeletionMode('all');
          setDateStart(null);
          setDateEnd(null);
        },
      },
    );
  };

  return (
    <div className="space-y-4">
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="flex w-full items-center justify-between rounded-md p-2 hover:bg-muted/50 transition-colors"
          >
            <div className="flex items-center gap-2">
              <Trash2 className="size-4 text-destructive" />
              <h3 className="text-sm font-semibold text-destructive">
                {t('admin.clearWorkspaceOpportunitiesAction.clearOpportunities')}
              </h3>
            </div>
            <ChevronDown
              className={`size-4 text-muted-foreground transition-transform ${
                isOpen ? 'rotate-180' : ''
              }`}
            />
          </button>
        </CollapsibleTrigger>

        <CollapsibleContent className="space-y-4 pt-2">
          <p className="text-xs text-muted-foreground">
            {t('admin.clearWorkspaceOpportunitiesAction.permanentlyDeleteOpportunitiesFromThis')}
          </p>

          <div className="space-y-4">
            {/* Deletion Mode */}
            <div className="space-y-3">
              <Label className="text-xs">
                {t('admin.clearWorkspaceOpportunitiesAction.deletionMode')}
              </Label>
              <RadioGroup
                value={deletionMode}
                onValueChange={(value) => setDeletionMode(value as 'dateRange' | 'all')}
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="dateRange" id={`date-range-mode-${workspaceId}`} />
                  <Label
                    htmlFor={`date-range-mode-${workspaceId}`}
                    className="text-xs font-normal cursor-pointer"
                  >
                    {t('admin.clearWorkspaceOpportunitiesAction.clearByDateRange')}
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  {/* eslint-disable-next-line i18next/no-literal-string -- radio option value attribute */}
                  <RadioGroupItem value="all" id={`all-mode-${workspaceId}`} />
                  <Label
                    htmlFor={`all-mode-${workspaceId}`}
                    className="text-xs font-normal cursor-pointer"
                  >
                    {t('admin.clearWorkspaceOpportunitiesAction.clearAllOpportunities')}
                  </Label>
                </div>
              </RadioGroup>
            </div>

            {/* Date Range Picker */}
            {deletionMode === 'dateRange' && (
              <div className="space-y-3">
                <Label className="text-xs">
                  {t('admin.clearWorkspaceOpportunitiesAction.dateRange')}
                </Label>
                <div className="flex items-center gap-3">
                  <div className="flex-1">
                    <Label
                      htmlFor={`date-start-${workspaceId}`}
                      className="text-xs text-muted-foreground"
                    >
                      {t('admin.clearWorkspaceOpportunitiesAction.startDate')}
                    </Label>
                    <DatePicker value={dateStart} onChange={setDateStart}>
                      <DatePicker.Trigger
                        placeholder={t('admin.clearWorkspaceOpportunitiesAction.startDate2')}
                      />
                      <DatePicker.Content
                        disabledDates={dateEnd ? { after: dateEnd } : undefined}
                      />
                    </DatePicker>
                  </div>
                  <div className="flex-1">
                    <Label
                      htmlFor={`date-end-${workspaceId}`}
                      className="text-xs text-muted-foreground"
                    >
                      {t('admin.clearWorkspaceOpportunitiesAction.endDate')}
                    </Label>
                    <DatePicker value={dateEnd} onChange={setDateEnd}>
                      <DatePicker.Trigger
                        placeholder={t('admin.clearWorkspaceOpportunitiesAction.endDate2')}
                      />
                      <DatePicker.Content
                        disabledDates={dateStart ? { before: dateStart } : undefined}
                      />
                    </DatePicker>
                  </div>
                </div>
              </div>
            )}

            {/* Preview Count */}
            <div className="rounded-md border border-border bg-muted/50 p-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Calendar className="size-3 text-muted-foreground" />
                  <span className="text-xs font-medium">
                    {t('admin.clearWorkspaceOpportunitiesAction.preview')}
                  </span>
                </div>
                {isLoadingPreview ? (
                  <Spinner className="size-3" />
                ) : deletionMode === 'dateRange' && (!dateStart || !dateEnd) ? (
                  <span className="text-xs text-muted-foreground italic">
                    {t('admin.clearWorkspaceOpportunitiesAction.selectADateRangeTo')}
                  </span>
                ) : (
                  <span className="text-xs font-semibold">
                    {t('admin.clearWorkspaceOpportunitiesAction.opportunitiesWillBeDeleted', {
                      count: previewCount ?? 0,
                    })}
                  </span>
                )}
              </div>
            </div>

            {/* Warning */}
            {canClear && previewCount === 0 && (
              <Alert variant="default" className="border-yellow-200 bg-yellow-50">
                <AlertTriangle className="size-3 text-yellow-600" />
                <AlertDescription className="text-xs text-yellow-800">
                  {t('admin.clearWorkspaceOpportunitiesAction.noOpportunitiesFoundMatchingYour')}
                </AlertDescription>
              </Alert>
            )}

            {/* Action Button */}
            <div className="flex items-center justify-end pt-2">
              <Button
                variant="destructive"
                size="sm"
                onClick={handleClearClick}
                disabled={!canClear || previewCount === 0 || isLoadingPreview}
              >
                {t('admin.clearWorkspaceOpportunitiesAction.clearOpportunities')}
              </Button>
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Confirmation Modal */}
      <ClearOpportunitiesConfirmationModal
        open={isConfirmModalOpen}
        onCancel={() => setIsConfirmModalOpen(false)}
        onConfirm={handleConfirmDelete}
        isDeleting={clearMutation.isPending}
        workspaceName={workspaceName}
        deletionMode={deletionMode}
        dateStart={dateStart}
        dateEnd={dateEnd}
        opportunitiesCount={previewCount ?? 0}
      />
    </div>
  );
};
